import React, { useMemo } from "react";

const gameScore = (team, bowlerId, gameIndex) => {
  const games = team?.transformedScores?.[bowlerId];
  if (!Array.isArray(games)) return "";
  const score = games[gameIndex]?.score;
  return score == null || score === "" ? "" : score;
};

const ScoreReviewGames = ({
  team,
  bowlers = [],
  gamesPerEvent = 3,
  canEnterScores = false,
  isTeamVerified = false,
  payoutsLocked = false,
  onScoreUpdate,
  getCalculationResult,
  isCalculatingTotals = false,
  teamScratchTotal = 0,
  teamHandicapTotal = 0,
  squadLabel = "",
}) => {
  const canEdit =
    canEnterScores && !isTeamVerified && !payoutsLocked && Boolean(onScoreUpdate);
  const gameCount = Math.max(1, gamesPerEvent || 3);
  const columns = useMemo(
    () => [
      "minmax(5.5rem, 1.4fr)",
      ...Array.from({ length: gameCount }, () => "minmax(2.75rem, 3.25rem)"),
      "3.25rem",
      "3.25rem",
    ],
    [gameCount]
  );
  const gridTemplateColumns = columns.join(" ");
  const totalClass = "font-score text-right tabular-nums text-brand-charcoal border-l border-brand-charcoal/10 pl-2";

  if (!bowlers.length) {
    return (
      <p className="p-4 text-sm text-brand-muted">No bowlers on this team.</p>
    );
  }

  return (
    <div className="p-3 sm:p-4">
      {squadLabel ? (
        <p className="text-xs text-brand-muted mb-3">{squadLabel}</p>
      ) : null}
      <div
        className="grid gap-x-2 gap-y-1 items-center text-xs font-display font-bold uppercase tracking-wider text-brand-muted"
        style={{ gridTemplateColumns }}
      >
        <span>Name</span>
        {Array.from({ length: gameCount }, (_, index) => (
          <span key={index} className="text-center">
            {index + 1}
          </span>
        ))}
        <span className="text-right border-l border-brand-charcoal/10 pl-2">
          Scratch
        </span>
        <span className="text-right">Hdcp</span>
      </div>
      <div className="mt-2 space-y-2">
        {bowlers.map((bowler) => {
          const scratch =
            getCalculationResult?.(team.id, bowler.id, "scratch")?.total || 0;
          const hdcpTot =
            getCalculationResult?.(team.id, bowler.id, "handicap")
              ?.handicapTotal || 0;
          return (
            <div
              key={bowler.id}
              className="grid gap-x-2 gap-y-1 items-center"
              style={{ gridTemplateColumns }}
            >
              <span className="text-sm font-medium text-brand-charcoal truncate">
                {bowler.name}
              </span>
              {Array.from({ length: gameCount }, (_, gameIndex) => {
                const value = gameScore(team, bowler.id, gameIndex);
                if (!canEdit) {
                  return (
                    <span
                      key={gameIndex}
                      className="font-score text-center text-brand-charcoal tabular-nums"
                    >
                      {value === "" ? "—" : value}
                    </span>
                  );
                }
                return (
                  <input
                    key={`${bowler.id}-${gameIndex}-${value}`}
                    type="number"
                    min="0"
                    max="300"
                    inputMode="numeric"
                    aria-label={`${bowler.name} game ${gameIndex + 1}`}
                    className="w-full h-8 px-0.5 border border-brand-charcoal/20 rounded-md bg-white text-center font-score text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                    defaultValue={value}
                    onBlur={(event) => {
                      const raw = event.target.value.trim();
                      const score = raw === "" ? null : parseInt(raw, 10);
                      if (raw !== "" && (Number.isNaN(score) || score < 0 || score > 300)) {
                        event.target.value = value === "" ? "" : String(value);
                        return;
                      }
                      if (String(score ?? "") === String(value)) return;
                      onScoreUpdate(team.id, bowler.id, gameIndex, score);
                    }}
                  />
                );
              })}
              <span className={totalClass}>
                {isCalculatingTotals ? "—" : scratch}
              </span>
              <span className="font-score text-right tabular-nums text-brand-charcoal">
                {isCalculatingTotals ? "—" : hdcpTot}
              </span>
            </div>
          );
        })}
      </div>
      <div
        className="grid gap-x-2 items-center mt-3 pt-3 border-t border-brand-charcoal/10"
        style={{ gridTemplateColumns }}
      >
        <span className="text-sm font-display font-bold text-brand-charcoal">
          Team
        </span>
        {Array.from({ length: gameCount }, (_, index) => (
          <span key={index} />
        ))}
        <span className={totalClass}>
          {isCalculatingTotals ? "—" : teamScratchTotal}
        </span>
        <span className="font-score text-right tabular-nums text-brand-charcoal">
          {isCalculatingTotals ? "—" : teamHandicapTotal}
        </span>
      </div>
    </div>
  );
};

export default ScoreReviewGames;
